// Match feed of a panel match - one component for the admin match page and the public match page.
// Data: GET /api/matches/:id/feed (services/matchFeed.js): the v1 event log, or older matches' kill feed
// rebuilt into the same events. Rounds newest first (a live match never needs auto-scroll); per round
// the winner, reason and score, then every kill (assists, flash assists, team kills, modifiers), bomb
// plays, pauses, halftime / side switch and restores; Zeus / knife kills and aces are highlighted.
// Before round 1: the knife round (its kills, winner, stay / switch). Admin: joins and leaves on demand.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { WeaponIcon } from '../ext/extUi';
import './feed.css';

const RESTORE_TYPES = new Set(['round_restored', 'map_reset']);
const MAP_LEVEL = new Set(['map_start', 'map_end', 'match_end', 'knife_winner', 'knife_decision', 'map_reset']);
const REASON_ICON = { bomb_exploded: '/killfeed/explosion.svg', bomb_defused: '/killfeed/defuse.svg', time: '/killfeed/stopwatch.svg' };
const isKnifeEv = (e) => !!(e.data && e.data.knife_round);
// within a round: by time, then id (map / match point events filled in later for older matches have higher ids)
const byTime = (a, b) => ((Number(a.ts_ms) || 0) - (Number(b.ts_ms) || 0)) || (a.id - b.id);
const shortMap = (n) => String(n || '').replace(/^(de_|cs_)/, '');
const mapTitle = (n) => shortMap(n).replace(/(^|_)(\w)/g, (_, s, c) => `${s ? ' ' : ''}${c.toUpperCase()}`);
const mmss = (ms) => { const s = Math.max(0, Math.round(ms / 1000)); return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`; };

function Name({ p }) {
  if (!p) return <span className="mf-name">?</span>;
  return <span className={`mf-name ${p.side === 'CT' ? 'ct' : p.side === 'T' ? 't' : ''}`}>{p.name}</span>;
}

function Mod({ src, alt, title }) {
  return <img className="mf-mod" src={src} alt={alt} title={title || alt} onError={(e) => { e.target.style.display = 'none'; }} />;
}

function KillRow({ e, clock, t }) {
  const d = e.data || {};
  const zeus = !d.team_kill && !d.knife_round && /^taser$/i.test(d.weapon || '');
  const knife = !d.team_kill && !d.knife_round && /knife|bayonet/i.test(d.weapon || '');
  return (
    <div className={`mf-row kill${d.team_kill ? ' tk' : ''}${zeus ? ' hl zeus' : knife ? ' hl knife' : ''}`}>
      {clock}
      <span className="mf-kill">
        {d.attacker_blind && <Mod src="/killfeed/shooter_blind.png" alt="blind" title={t('ext.feed.blind', 'Attacker blind')} />}
        <Name p={d.attacker} />
        {/* assist right after the killer, like the in-game feed ("Killer + Assister [weapon] Victim"), whichever team the assister is on */}
        {d.assister && <span className="mf-assist">+ <Name p={d.assister} /></span>}
        {d.flash_assister && <span className="mf-assist">+ <Mod src="/killfeed/flashbang.png" alt="flash" title={t('feed.flashAssist', 'Flash assist')} /><Name p={d.flash_assister} /></span>}
        {d.in_air && <Mod src="/killfeed/inairkill.svg" alt="air" title={t('ext.feed.inAir', 'In the air')} />}
        <span className="mf-wpn"><WeaponIcon name={d.weapon} height={15} /></span>
        {d.noscope && <Mod src="/killfeed/noscope.png" alt="noscope" title={t('feed.noscope', 'No-scope')} />}
        {d.through_smoke && <Mod src="/killfeed/through_smoke.png" alt="smoke" title={t('feed.throughSmoke', 'Through smoke')} />}
        {d.penetrated && <Mod src="/killfeed/penetration.png" alt="wallbang" title={t('feed.wallbang', 'Wallbang')} />}
        {d.headshot && <Mod src="/killfeed/headshot.png" alt="HS" title={t('feed.headshot', 'Headshot')} />}
        <Name p={d.victim} />
      </span>
      {d.team_kill && <span className="mf-tag bad">{t('ext.feed.teamKill', 'Team kill')}</span>}
      {zeus && <span className="mf-tag zeus">{t('ext.feed.zeusKill', 'Zeus kill')}</span>}
      {knife && <span className="mf-tag knife">{t('ext.feed.knifeKill', 'Knife kill')}</span>}
    </div>
  );
}

function EventRow({ e, clock, teams, t }) {
  const d = e.data || {};
  const team = (k) => (teams[k] ? teams[k].name : k);
  const line = (cls, icon, content) => (
    <div className={`mf-row ev ${cls}`}>{clock}{icon && <Mod src={icon} alt="" />}<span className="mf-text">{content}</span></div>
  );
  const pill = (cls, content) => <div className="mf-row ev">{clock}<span className={`mf-tag ${cls}`}>{content}</span></div>;
  switch (e.type) {
    case 'kill': return <KillRow e={e} clock={clock} t={t} />;
    case 'suicide':
      return line('', null, <><Name p={d.player} /> {t('feed.diedTo', 'died ({{cause}})', { cause: d.cause || 'world' })}</>);
    case 'bomb_plant':
      return line('t', '/killfeed/bombplant.png', d.site
        ? t('ext.feed.planted', '{{name}} planted the bomb on {{site}}', { name: d.player ? d.player.name : 'T', site: d.site })
        : t('feed.plantedNoSite', '{{name}} planted the bomb', { name: d.player ? d.player.name : 'T' }));
    case 'bomb_defuse_start':
      return line('ct soft', '/killfeed/defuse.svg', <>{t('ext.feed.defuseStart', '{{name}} started defusing', { name: d.player ? d.player.name : 'CT' })} <span className={`mf-tag ${d.with_kit ? 'good' : ''}`}>{d.with_kit ? t('ext.feed.withKit', 'with kit') : t('ext.feed.withoutKit', 'no kit')}</span></>);
    case 'bomb_defuse':
      // CS2 logs no defuser: older rounds (before the parser credited who began defusing) have none
      return line('ct', '/killfeed/defuse.svg', d.player ? t('ext.feed.defused', '{{name}} defused the bomb', { name: d.player.name }) : t('feed.defusedNoName', 'The bomb was defused'));
    case 'bomb_explode':
      return line('t', '/killfeed/explosion.svg', d.site ? t('ext.feed.explodedSite', 'C4 exploded on bombsite {{site}}', { site: d.site }) : t('ext.feed.exploded', 'The bomb exploded'));
    case 'ace': return pill('ace', t('ext.feed.ace', '{{name}} ACE', { name: d.player ? d.player.name : '?' }));
    case 'clutch_start': return pill('warn', t('ext.feed.clutchStart', '{{name}} is in a 1v{{vs}}', { name: d.player ? d.player.name : '?', vs: d.vs }));
    case 'clutch_won': return pill('good', t('ext.feed.clutch', '{{name}} won a 1v{{vs}}', { name: d.player ? d.player.name : '?', vs: d.vs }));
    case 'clutch_lost': return pill('bad', t('ext.feed.clutchLost', '{{name}} lost the 1v{{vs}}', { name: d.player ? d.player.name : '?', vs: d.vs }));
    case 'map_point':
    case 'match_point':
      return pill(e.type === 'match_point' ? 'bad' : 'warn', <>
        {e.type === 'match_point' ? t('ext.feed.matchPoint', 'Match point: {{team}}', { team: team(d.team) }) : t('ext.feed.mapPoint', 'Map point: {{team}}', { team: team(d.team) })}
        {d.overtime ? ` (${t('ext.feed.overtime', 'overtime')})` : ''}
      </>);
    case 'pause_start': {
      const kind = { tactical: t('feed.pauseTactical', 'Tactical timeout'), technical: t('feed.pauseTechnical', 'Technical pause'), admin: t('feed.pauseAdmin', 'Admin pause'), production: t('feed.pauseProduction', 'Production pause') }[d.type] || t('feed.pause', 'Pause');
      return pill('warn', d.team ? `${kind}: ${team(d.team)}` : kind);
    }
    case 'pause_end':
      return pill('', d.duration_s != null ? t('ext.feed.unpause', 'Unpaused after {{s}}s', { s: d.duration_s }) : t('feed.unpaused', 'Unpaused'));
    case 'halftime_start': return pill('info', t('ext.phase.halftime', 'Halftime'));
    case 'side_switch': return pill('info', t('ext.feed.sideSwitch', 'Sides switched'));
    case 'round_restored': return pill('warn', d.to_round ? t('ext.feed.restored', 'Restored to round {{n}}', { n: d.to_round }) : t('feed.restoredPlain', 'Round restored'));
    case 'map_reset': return pill('warn', t('feed.mapReset', 'Map reset by an admin'));
    case 'knife_winner': return pill('info', t('ext.feed.knifeWinner', '{{team}} won the knife round', { team: team(d.team) }));
    case 'knife_decision':
      return pill('info', d.choice === 'switch' ? t('ext.feed.knifeSwitch', '{{team}} chose to switch sides', { team: team(d.team) }) : t('ext.feed.knifeStay', '{{team}} chose to stay', { team: team(d.team) }));
    // joins / leaves at any time of the match (warmup ones sit under the rounds, with no round)
    case 'player_connect':
    case 'player_disconnect': {
      const join = e.type === 'player_connect';
      return (
        <div className={`mf-row ev conn ${join ? 'in' : 'out'}`}>
          {clock}
          <span className="mf-conn-ic" aria-hidden="true">{join ? '→' : '←'}</span>
          <span className="mf-text"><Name p={d.player} /> {join ? t('feed.joinedShort', 'connected') : t('feed.leftShort', 'disconnected')}</span>
          {d.player && d.player.team && teams[d.player.team] && <span className="mf-conn-team">{teams[d.player.team].name}</span>}
        </div>
      );
    }
    default: return null;
  }
}

/**
 * @param {object} props
 * @param {number|string} props.matchId
 * @param {object}  [props.api]        axios instance (public page: its public client)
 * @param {boolean} [props.admin]      owner view (kept for callers; the server decides what each viewer gets)
 * @param {number|string|null} [props.mapIndex]  map chosen by the page ('all' / null = the feed's own map tabs)
 * @param {boolean} [props.live]       poll every 5 s while the match is live
 * @param {number}  [props.refreshKey] bump to fetch new events now (page WebSocket triggers)
 */
export default function MatchFeed({ matchId, api = axios, admin = false, mapIndex = null, live = false, refreshKey = 0 }) {
  const { t } = useTranslation();
  const [feed, setFeed] = useState(null);
  const [events, setEvents] = useState([]);
  const [error, setError] = useState(null);
  const [ownMap, setOwnMap] = useState(null);
  const feedRef = useRef(null);
  const busy = useRef(false);

  const load = useCallback(async (full) => {
    if (busy.current) return;
    busy.current = true;
    try {
      const cur = feedRef.current;
      const since = !full && cur && cur.source === 'v1' ? cur.last_id : 0;
      const r = await api.get(`/api/matches/${matchId}/feed`, { params: since ? { since } : {} });
      const d = r.data || {};
      const evs = d.events || [];
      if (since && evs.some(e => RESTORE_TYPES.has(e.type))) { busy.current = false; return load(true); } // earlier events were voided
      const { events: _drop, ...meta } = d;
      feedRef.current = { ...meta, last_id: since && !evs.length ? cur.last_id : d.last_id };
      setFeed(feedRef.current);
      setEvents(prev => (since ? [...prev, ...evs.filter(e => !prev.some(p => p.id === e.id))] : evs));
      setError(null);
    } catch (e) {
      setError(e.response && e.response.status === 403 ? 'hidden' : 'failed');
    } finally { busy.current = false; }
  }, [api, matchId]);

  useEffect(() => { feedRef.current = null; setEvents([]); setFeed(null); load(true); }, [load]);
  useEffect(() => {
    if (!refreshKey) return undefined;
    const id = setTimeout(() => load(false), 300); // a burst of WS messages = one fetch
    return () => clearTimeout(id);
  }, [refreshKey, load]);
  useEffect(() => {
    if (!live) return undefined;
    const id = setInterval(() => load(false), 5000);
    return () => clearInterval(id);
  }, [live, load]);

  const teams = (feed && feed.teams) || { team1: { name: 'Team 1' }, team2: { name: 'Team 2' } };
  const parentMap = mapIndex === 'all' || mapIndex === null || mapIndex === undefined ? null : parseInt(mapIndex, 10);
  const mapIdxs = useMemo(() => [...new Set(events.map(e => (e.map_index == null ? 0 : e.map_index)))].sort((a, b) => a - b), [events]);
  const curMap = parentMap != null ? parentMap
    : ownMap != null && mapIdxs.includes(ownMap) ? ownMap
      : (feed && mapIdxs.includes(feed.current_map_index) ? feed.current_map_index : mapIdxs[mapIdxs.length - 1] ?? 0);

  const view = useMemo(() => {
    const evs = events.filter(e => (e.map_index == null ? 0 : e.map_index) === curMap);
    const rounds = new Map();
    const mapLevel = [], knife = [];
    for (const e of evs) {
      if (isKnifeEv(e)) { knife.push(e); continue; }
      if (e.round == null || MAP_LEVEL.has(e.type)) { mapLevel.push(e); continue; }
      if (!rounds.has(e.round)) rounds.set(e.round, { start: null, end: null, events: [] });
      const r = rounds.get(e.round);
      if (e.type === 'round_start') { if (!r.start) r.start = e; } else if (e.type === 'round_end') r.end = e;
      else r.events.push(e);
    }
    for (const r of rounds.values()) r.events.sort(byTime);
    // aces for rounds without an ace event (older matches): the 5th enemy kill of one player
    for (const r of rounds.values()) {
      if (r.events.some(e => e.type === 'ace')) continue;
      const n = new Map();
      for (let i = 0; i < r.events.length; i++) {
        const e = r.events[i];
        if (e.type !== 'kill' || !e.data || e.data.team_kill || !e.data.attacker) continue;
        const k = e.data.attacker.steam_id || e.data.attacker.name;
        n.set(k, (n.get(k) || 0) + 1);
        if (n.get(k) === 5) { r.events.splice(i + 1, 0, { id: `ace-${e.id}`, type: 'ace', ts_ms: e.ts_ms, data: { player: e.data.attacker } }); i++; }
      }
    }
    const ends = mapLevel.filter(e => e.type === 'map_end' || e.type === 'match_end');
    const isConn = (e) => e.type === 'player_connect' || e.type === 'player_disconnect';
    const before = mapLevel.filter(e => e.type !== 'map_end' && e.type !== 'match_end' && e.type !== 'map_start' && !isConn(e));
    const warmup = mapLevel.filter(isConn); // joins / leaves outside a round (warmup, between maps)
    return { rounds, ends, before, knife, warmup };
  }, [events, curMap]);

  const roundNums = [...view.rounds.keys()].sort((a, b) => b - a);
  // warmup joins / leaves: open until a round is played (warmup followed live), then collapsed
  const [warmupOpen, setWarmupOpen] = useState(null);
  const showWarmup = warmupOpen == null ? roundNums.length === 0 : warmupOpen;
  const latestRound = roundNums[0];
  const mapName = feed && feed.maps && feed.maps[curMap] ? mapTitle(feed.maps[curMap].name) : '';

  const clockFor = (start) => (e) => (start && e.ts_ms >= start.ts_ms
    ? <span className="mf-clock">{mmss(e.ts_ms - start.ts_ms)}</span>
    : <span className="mf-clock" />);

  if (error === 'hidden') return null;

  return (
    <div className="mf">
      <div className="mf-bar">
        {parentMap == null && mapIdxs.length > 1 && (
          <div className="mf-maps" role="tablist">
            {mapIdxs.map(mi => (
              <button key={mi} type="button" role="tab" aria-selected={mi === curMap} className={mi === curMap ? 'on' : ''} onClick={() => setOwnMap(mi)}>
                {feed && feed.maps && feed.maps[mi] ? mapTitle(feed.maps[mi].name) : t('ext.mapN', 'Map {{n}}', { n: mi + 1 })}
              </button>
            ))}
          </div>
        )}
        <span className="mf-spacer" />
        {feed && feed.delay_s > 0 && <span className="mf-note">{t('feed.delayed', '{{s}} s delay', { s: feed.delay_s })}</span>}
      </div>

      {error === 'failed' && !events.length && <div className="mf-empty">{t('feed.loadFailed', 'The feed could not be loaded.')}</div>}
      {!error && feed && !roundNums.length && !view.before.length && !view.knife.length && !view.ends.length && (
        <div className="mf-empty">{t('feed.empty', 'Kills, bomb plays and round results appear here as the map is played.')}</div>
      )}

      {view.ends.map(e => (
        <div key={e.id} className="mf-result">
          {e.type === 'map_end'
            ? t('ext.feed.mapEnd', '{{map}} won by {{team}} {{s1}}-{{s2}}', { map: mapTitle(e.data.map) || mapName, team: teams[e.data.winner] ? teams[e.data.winner].name : '—', s1: e.data.score ? e.data.score.team1 : '', s2: e.data.score ? e.data.score.team2 : '' })
            : t('ext.feed.matchEnd', 'Match won by {{team}}', { team: teams[e.data.winner] ? teams[e.data.winner].name : '—' })}
        </div>
      ))}

      {roundNums.map(n => {
        const r = view.rounds.get(n);
        const end = r.end && r.end.data;
        const side = end && (end.winner_side === 'CT' ? 'ct' : end.winner_side === 'T' ? 't' : '');
        const running = !end && n === latestRound && live;
        const kills = r.events.filter(e => e.type === 'kill').length;
        const clock = clockFor(r.start);
        return (
          <section key={n} className={`mf-round ${side || ''}${running ? ' running' : ''}`}>
            <header className="mf-round-head">
              <span className="mf-round-n">{t('ext.roundN', 'Round {{n}}', { n })}</span>
              {end ? (
                <>
                  <img className="mf-reason-ic" src={REASON_ICON[end.reason] || (end.winner_side === 'CT' ? '/killfeed/skull_t.svg' : '/killfeed/skull_ct.svg')} alt="" />
                  <span className={`mf-winner ${side}`}>{teams[end.winner] ? teams[end.winner].name : end.winner_side}</span>
                  <span className="mf-reason">{t(`ext.reason.${end.reason}`, end.reason)}</span>
                  <span className="mf-spacer" />
                  {end.score && <span className="mf-score">{end.score.team1}<i>–</i>{end.score.team2}</span>}
                </>
              ) : (
                <><span className={`mf-live${running ? '' : ' idle'}`}>{running ? t('feed.live', 'Live') : t('ext.inProgress', 'In progress')}</span><span className="mf-spacer" /></>
              )}
              <span className="mf-count">{t('ext.killsN', '{{n}} kills', { n: kills })}</span>
            </header>
            <div className="mf-rows">
              {r.events.length === 0 && <div className="mf-row ev soft"><span className="mf-text">{t('ext.noEventsYet', 'Nothing yet.')}</span></div>}
              {r.events.map(e => <EventRow key={e.id} e={e} clock={clock(e)} teams={teams} t={t} />)}
            </div>
          </section>
        );
      })}

      {view.before.length > 0 && (
        <div className="mf-before">
          {[...view.before].reverse().map(e => <EventRow key={e.id} e={e} clock={<span className="mf-clock" />} teams={teams} t={t} />)}
        </div>
      )}

      {view.knife.length > 0 && (
        <section className="mf-round knife">
          <header className="mf-round-head">
            <span className="mf-round-n">{t('matchDetail.knifeRound', 'Knife Round')}</span>
            <span className="mf-reason">{t('ext.feed.warmupNote', 'not counted in stats')}</span>
            <span className="mf-spacer" />
            <span className="mf-count">{t('ext.killsN', '{{n}} kills', { n: view.knife.filter(e => e.type === 'kill').length })}</span>
          </header>
          <div className="mf-rows">
            {view.knife.map(e => <EventRow key={e.id} e={e} clock={<span className="mf-clock" />} teams={teams} t={t} />)}
          </div>
        </section>
      )}

      {view.warmup.length > 0 && (
        <section className="mf-round warmup">
          <button type="button" className="mf-round-head mf-fold" aria-expanded={showWarmup} onClick={() => setWarmupOpen(!showWarmup)}>
            <span className={`mf-caret${showWarmup ? ' open' : ''}`} aria-hidden="true">▶</span>
            <span className="mf-round-n">{t('ext.phase.warmup', 'Warmup')}</span>
            <span className="mf-reason">{t('feed.warmupConns', 'connects & disconnects')}</span>
            <span className="mf-spacer" />
            <span className="mf-count">{view.warmup.length}</span>
          </button>
          {showWarmup && (
            <div className="mf-rows">
              {[...view.warmup].reverse().map(e => <EventRow key={e.id} e={e} clock={<span className="mf-clock" />} teams={teams} t={t} />)}
            </div>
          )}
        </section>
      )}
    </div>
  );
}
